import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TenantService } from '../../common/services/tenant.service';

/**
 * Shown when this tab keeps working in an organization other than the
 * persisted active one (another tab switched organizations).
 */
@Component({
  selector: 'app-tenant-banner',
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <div class="tenant-banner" role="status">
        <mat-icon aria-hidden="true">swap_horiz</mat-icon>
        <span>
          This tab is working in <strong>{{ tabName() }}</strong>.
          Your active organization is <strong>{{ activeName() }}</strong>.
        </span>
        <button type="button" class="tenant-banner__action" [disabled]="busy()" (click)="adoptActive()">
          Switch this tab to {{ activeName() }}
        </button>
      </div>
    }
  `,
  styles: [`
    .tenant-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: #fff8e1;
      border-bottom: 1px solid #ffe082;
      color: #5d4037;
      font-size: 14px;
    }
    .tenant-banner__action {
      margin-left: auto;
      border: 1px solid #8d6e63;
      background: transparent;
      border-radius: 4px;
      padding: 4px 10px;
      cursor: pointer;
      color: inherit;
    }
  `],
})
export class TenantBannerComponent {
  private tenant = inject(TenantService);
  readonly busy = signal(false);

  readonly visible = computed(() => this.tenant.loaded() && this.tenant.isImpersonating());
  readonly tabName = computed(() => this.tenant.currentMembership()?.name ?? 'another organization');
  readonly activeName = computed(() => this.tenant.activeMembership()?.name ?? 'another organization');

  async adoptActive(): Promise<void> {
    const active = this.tenant.activeAccountId();
    if (!active) return;
    this.busy.set(true);
    try {
      await this.tenant.load();
      this.tenant.pinTabToActive();
      globalThis.location?.reload();
    } finally {
      this.busy.set(false);
    }
  }
}
